import { useState, useEffect, createContext } from "react"

// import exports from '../../src/aws-exports'
import { useAuthenticator } from '@aws-amplify/ui-react'

import {
  getWebhookEndpointSecret as getWebhookEndpointSecretQuery,
  listWebhookEndpoints,
  listWebhookEventTypes,
} from '../../src/graphql/queries'
import {
  createWebhookEndpoint,
  deleteWebhookEndpoint,
  rotateWebhookEndpointSecret,
  setWebhookEndpointPurpose,
  updateWebhookEndpoint,
} from '../../src/graphql/mutations'
import { generateClient } from '@aws-amplify/api'

const WebhookPortalContext = createContext()
export default WebhookPortalContext

const ContextState = () => {
  const { user } = useAuthenticator((context) => [context.user])

  // Migrating from Amplify V5 to V6.
  // https://docs.amplify.aws/react/migrate-v5-to-v6/migrate-graphql-api/
  const client = generateClient();
  
  const [endpoints, setEndpoints] = useState([])
  const [eventTypes, setEventTypes] = useState([])
  const [endpointsLoading, setEndpointsLoading] = useState(true)
  const [eventTypesLoading, setEventTypesLoading] = useState(true)
  const [endpointsError, setEndpointsError] = useState(null)
  const [eventTypesError, setEventTypesError] = useState(null)

  const loadEndpoints = async () => {
    if (!user) {
      setEndpointsLoading(false)
      return
    }
    setEndpointsLoading(true)
    setEndpointsError(null)
    try {
      const result = await client.graphql({
        query: listWebhookEndpoints,
        authMode: 'userPool',
      })
      if (!Array.isArray(result?.data?.listWebhookEndpoints)) {
        throw new Error('Webhook endpoint response is invalid')
      }
      setEndpoints(result.data.listWebhookEndpoints)
    } catch (error) {
      setEndpointsError(error instanceof Error ? error : new Error('Webhook endpoints could not be loaded'))
      throw error
    } finally {
      setEndpointsLoading(false)
    }
  }

  const loadEventTypes = async () => {
    if (!user) {
      setEventTypesLoading(false)
      return
    }
    setEventTypesLoading(true)
    setEventTypesError(null)
    try {
      const result = await client.graphql({ query: listWebhookEventTypes, authMode: 'userPool' })
      if (!Array.isArray(result?.data?.listWebhookEventTypes)) {
        throw new Error('Webhook event-type response is invalid')
      }
      setEventTypes(result.data.listWebhookEventTypes)
    } catch (error) {
      setEventTypesError(error instanceof Error ? error : new Error('Webhook event types could not be loaded'))
      throw error
    } finally {
      setEventTypesLoading(false)
    }
  }

  const refreshEndpointsAfterAcknowledgedMutation = async () => {
    try {
      await loadEndpoints()
    } catch {
      // loadEndpoints records the refresh failure and preserves the last good
      // endpoint list. The acknowledged mutation itself remains successful.
    }
  }

  const setEndpointPurpose = async ({ endpointId, purpose }) => {
    await client.graphql({
      query: setWebhookEndpointPurpose,
      authMode: 'userPool',
      variables: { input: { endpointId, purpose } },
    })
    await refreshEndpointsAfterAcknowledgedMutation()
  }

  const createEndpoint = async (input) => {
    await client.graphql({ query: createWebhookEndpoint, authMode: 'userPool', variables: { input } })
    await refreshEndpointsAfterAcknowledgedMutation()
  }

  const updateEndpoint = async (input) => {
    await client.graphql({ query: updateWebhookEndpoint, authMode: 'userPool', variables: { input } })
    await refreshEndpointsAfterAcknowledgedMutation()
  }

  const deleteEndpoint = async (endpointId) => {
    await client.graphql({ query: deleteWebhookEndpoint, authMode: 'userPool', variables: { endpointId } })
    await refreshEndpointsAfterAcknowledgedMutation()
  }

  const getEndpointSecret = async (endpointId) => {
    const result = await client.graphql({
      query: getWebhookEndpointSecretQuery,
      authMode: 'userPool',
      variables: { endpointId },
    })
    return result.data.getWebhookEndpointSecret.secret
  }

  const rotateEndpointSecret = async (endpointId) => {
    const result = await client.graphql({
      query: rotateWebhookEndpointSecret,
      authMode: 'userPool',
      variables: { endpointId },
    })
    return result.data.rotateWebhookEndpointSecret.secret
  }

  useEffect(() => {
    loadEndpoints().catch(() => {})
    loadEventTypes().catch(() => {})
  }, [user])

  return {
    endpoints,
    eventTypes,
    endpointsLoading,
    eventTypesLoading,
    endpointsError,
    eventTypesError,
    loadEndpoints,
    loadEventTypes,
    setEndpointPurpose,
    createEndpoint,
    updateEndpoint,
    deleteEndpoint,
    getEndpointSecret,
    rotateEndpointSecret,
  }
}

const WebhookPortalContextProvider = (props) => {
  const state = ContextState()

  return (
    <WebhookPortalContext.Provider value={state}>
      {props.children}
    </WebhookPortalContext.Provider>
  ) 
}

export { WebhookPortalContext, WebhookPortalContextProvider }
